import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../api/endpoints';
import type { UsersQuery, ApiUser } from '../api/types';
import { toast } from 'sonner';

/**
 * Query key factory for users
 */
export const usersKeys = {
  all: ['users'] as const,
  lists: () => [...usersKeys.all, 'list'] as const,
  list: (query?: UsersQuery) => [...usersKeys.lists(), query] as const,
};

/**
 * Hook to fetch users with filtering and pagination
 *
 * Features:
 * - Fetches users with optional filtering by email, name, and tags
 * - Supports cursor-based pagination
 * - 2-minute staleTime for relatively stable data
 * - Admin only endpoint
 *
 * @param query - Query parameters for filtering and pagination
 * @param enabled - Whether the query should run (default: true)
 * @returns Users data with loading and error states
 *
 * @example
 * ```tsx
 * const { users, pagination, isLoading, error } = useUsers({
 *   email: 'john',
 *   limit: 20
 * });
 * ```
 */
export function useUsers(query?: UsersQuery, enabled = true) {
  const queryResult = useQuery({
    queryKey: usersKeys.list(query),
    queryFn: () => usersApi.getUsers(query),
    staleTime: 1000 * 10, // 2 minutes
    enabled,
  });

  return {
    users: queryResult.data?.users ?? [],
    pagination: queryResult.data?.pagination ?? {
      limit: 20,
      nextCursor: null,
      prevCursor: null,
      hasMore: false,
    },
    isLoading: queryResult.isLoading,
    error: queryResult.error as Error | null,
    refetch: queryResult.refetch,
  };
}

/**
 * Hook to manage users with mutations
 *
 * Features:
 * - Update user information
 * - Delete users
 * - Optimistic updates for better UX
 * - Toast notifications on success/error
 * - Invalidates queries on mutation success
 *
 * @returns Mutation functions for user management
 *
 * @example
 * ```tsx
 * const { updateUser, deleteUser } = useUserManagement();
 *
 * await updateUser(123, { name: 'John Doe', approved: true });
 * await deleteUser(456);
 * ```
 */
export function useUserManagement() {
  const queryClient = useQueryClient();

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async ({
      userId,
      data,
    }: {
      userId: number;
      data: {
        name?: string;
        email?: string;
        image?: string | null;
        tags?: string[];
        approved?: boolean;
        data?: Record<string, unknown>;
      };
    }) => usersApi.updateUser(userId, data),
    onMutate: async ({ userId, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: usersKeys.lists() });

      // Snapshot previous values
      const previousQueries = queryClient.getQueriesData({ queryKey: usersKeys.lists() });

      // Optimistically update user in all queries
      queryClient.setQueriesData({ queryKey: usersKeys.lists() }, (old: any) => {
        if (!old?.users) return old;

        return {
          ...old,
          users: old.users.map((user: ApiUser) =>
            user.id === userId ? { ...user, ...data } : user
          ),
        };
      });

      return { previousQueries };
    },
    onError: (error: any, _variables, context) => {
      // Rollback on error
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      const errorMessage = error?.message || 'Failed to update user';
      toast.error(errorMessage);
    },
    onSuccess: (updatedUser) => {
      // Update cache with server response
      queryClient.setQueriesData({ queryKey: usersKeys.lists() }, (old: any) => {
        if (!old?.users) return old;

        return {
          ...old,
          users: old.users.map((user: ApiUser) =>
            user.id === updatedUser.id ? updatedUser : user
          ),
        };
      });

      toast.success('User updated successfully');
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: number) => usersApi.deleteUser(userId),
    onMutate: async (userId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: usersKeys.lists() });

      // Snapshot previous values
      const previousQueries = queryClient.getQueriesData({ queryKey: usersKeys.lists() });

      // Optimistically remove user from all queries
      queryClient.setQueriesData({ queryKey: usersKeys.lists() }, (old: any) => {
        if (!old?.users) return old;

        return {
          ...old,
          users: old.users.filter((user: ApiUser) => user.id !== userId),
        };
      });

      return { previousQueries };
    },
    onError: (error: any, _variables, context) => {
      // Rollback on error
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      const errorMessage = error?.message || 'Failed to delete user';
      toast.error(errorMessage);
    },
    onSuccess: () => {
      toast.success('User deleted successfully');
      // No need to invalidate - optimistic update already removed the user
    },
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      email: string;
      image?: string | null;
      approved?: boolean;
      data?: Record<string, unknown>;
    }) => usersApi.createUser(data),
    onSuccess: (newUser) => {
      // Add new user to all queries
      queryClient.setQueriesData({ queryKey: usersKeys.lists() }, (old: any) => {
        if (!old?.users) return old;

        return {
          ...old,
          users: [newUser, ...old.users],
        };
      });

      toast.success('User created successfully');
    },
    onError: (error: any) => {
      const errorMessage = error?.message || 'Failed to create user';
      toast.error(errorMessage);
    },
  });

  return {
    updateUser: async (
      userId: number,
      data: {
        name?: string;
        email?: string;
        image?: string | null;
        tags?: string[];
        approved?: boolean;
        data?: Record<string, unknown>;
      }
    ) => {
      await updateUserMutation.mutateAsync({ userId, data });
    },
    deleteUser: async (userId: number) => {
      await deleteUserMutation.mutateAsync(userId);
    },
    createUser: async (data: {
      name: string;
      email: string;
      image?: string | null;
      approved?: boolean;
      data?: Record<string, unknown>;
    }) => {
      await createUserMutation.mutateAsync(data);
    },
    isUpdating: updateUserMutation.isPending,
    isDeleting: deleteUserMutation.isPending,
    isCreating: createUserMutation.isPending,
  };
}
